import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  LucideAngularModule,
  CheckCircle,
  Copy,
  Check,
  FlaskConical,
} from 'lucide-angular';
import { AssignmentService } from '../../services/assignment.service';
import { ASSIGNMENT_THEME_COLOR } from '../../services/assignment.models';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';

@Component({
  selector: 'app-step-ready',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './step-ready.html',
  styleUrl: './step-ready.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepReadyComponent implements OnInit {
  private readonly assignmentService = inject(AssignmentService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly router = inject(Router);

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;

  protected readonly icons = {
    CheckCircle,
    Copy,
    Check,
    FlaskConical,
  };

  ngOnInit(): void {
    this.amplitudeService.trackEvent('assignment_ready_step_viewed', {
      assignment_id: this.assignmentService.assignmentId,
    });
  }

  /** Currently active (expanded) card: 1-3 */
  private readonly activeCard$$ = new BehaviorSubject<number>(1);
  protected readonly activeCard$ = this.activeCard$$.asObservable();

  // Copied state
  private readonly isCopied$$ = new BehaviorSubject<boolean>(false);
  protected readonly isCopied$ = this.isCopied$$.asObservable();

  // Get share link
  protected readonly shareLink$: Observable<string> =
    this.assignmentService.shareCode$.pipe(
      map((shareCode) => {
        if (!shareCode) return '';
        const baseUrl =
          typeof window !== 'undefined' ? window.location.origin : '';
        return `${baseUrl}/a/${shareCode}`;
      }),
    );

  // Get truncated link for display
  protected readonly displayLink$: Observable<string> = this.shareLink$.pipe(
    map((link) => {
      if (!link) return '';
      if (link.length > 35) {
        return link.substring(0, 32) + '...';
      }
      return link;
    }),
  );

  protected onContinue(): void {
    const current = this.activeCard$$.value;
    if (current < 3) {
      this.amplitudeService.trackEvent('assignment_ready_step_continued', {
        assignment_id: this.assignmentService.assignmentId,
        from_card: current,
        to_card: current + 1,
      });
      this.activeCard$$.next(current + 1);
    }
  }

  protected async copyLink(source: 'card' | 'footer'): Promise<void> {
    const shareCode = this.assignmentService.shareCode;
    if (!shareCode) return;

    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : '';
    const fullLink = `${baseUrl}/a/${shareCode}`;

    try {
      await navigator.clipboard.writeText(fullLink);
      this.isCopied$$.next(true);

      this.amplitudeService.trackEvent('assignment_share_link_copied', {
        assignment_id: this.assignmentService.assignmentId,
        source,
      });

      setTimeout(() => {
        this.isCopied$$.next(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }

  protected onTestAssignment(): void {
    const shareCode = this.assignmentService.shareCode;
    if (!shareCode) return;

    this.amplitudeService.trackEvent('assignment_test_clicked', {
      assignment_id: this.assignmentService.assignmentId,
      source: 'step_ready',
    });

    this.router.navigate(['/a', shareCode]);
  }

  protected onDone(): void {
    this.amplitudeService.trackEvent('assignment_wizard_completed', {
      assignment_id: this.assignmentService.assignmentId,
    });

    this.assignmentService.reset();
    this.router.navigate(['/teacher/dashboard/assignments']);
  }
}
